/*
  <ia-slider-tabs> — tabbed product carousel controller.
  Toggles the active tab/panel, routes one shared pair of arrows to the active
  panel's <ia-slider>, and greys the arrows at the track ends. It creates no DOM:
  buttons, panels and sliders are all server-rendered. A panel's slider that
  initialised while hidden (width 0) is recalculated with slider.update() on reveal.
  An AB Tasty recommendation rebuilds a panel's slider on a new keen instance, so
  the arrow handlers re-bind on ia-reco:rendered.
*/

if (!customElements.get('ia-slider-tabs')) {
  class IaSliderTabs extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('.ia-slider-tabs__item'));
      this.tabs = Array.from(this.querySelectorAll('.ia-slider-tabs__tab'));
      this.panels = Array.from(this.querySelectorAll('.ia-slider-tabs__panel'));
      this.prevButton = this.querySelector('[data-tabs-prev]');
      this.nextButton = this.querySelector('[data-tabs-next]');
      this.boundSliders = new WeakSet();
      this.activeIndex = -1;

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.activateTab(index));
      });
      this.prevButton?.addEventListener('click', () => this.activeSlider?.prev());
      this.nextButton?.addEventListener('click', () => this.activeSlider?.next());
      this.addEventListener('ia-reco:rendered', () => {
        this.refreshSlider();
        this.updateArrows();
      });

      // Theme editor: follow the block the merchant selects.
      if (window.Shopify && Shopify.designMode) {
        this.blockSelectHandler = this.handleBlockSelect.bind(this);
        document.addEventListener('shopify:block:select', this.blockSelectHandler);
      }

      this.activateTab(0);
    }

    disconnectedCallback() {
      if (this.blockSelectHandler) {
        document.removeEventListener('shopify:block:select', this.blockSelectHandler);
      }
    }

    get activeSlider() {
      const panel = this.panels[this.activeIndex];
      return panel ? panel.querySelector('ia-slider') : null;
    }

    activateTab(index) {
      if (index < 0 || index >= this.panels.length) return;
      this.activeIndex = index;

      this.tabs.forEach((tab, i) => {
        const active = i === index;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      this.panels.forEach((panel, i) => {
        panel.classList.toggle('is-active', i === index);
      });

      this.refreshSlider();
      this.updateArrows();
    }

    refreshSlider() {
      const sliderEl = this.activeSlider;
      const keen = sliderEl && sliderEl.slider;
      if (!keen) return;
      // Recalculate now that the panel is visible (it may have been measured at
      // width 0 while hidden, on load or on a resize).
      keen.update();
      // Keyed on the keen instance, not the element: a re-initialised slider is
      // a new instance behind the same element and needs its own handlers.
      if (!this.boundSliders.has(keen)) {
        keen.on('slideChanged', () => this.updateArrows());
        keen.on('updated', () => this.updateArrows());
        this.boundSliders.add(keen);
      }
    }

    updateArrows() {
      if (!this.prevButton || !this.nextButton) return;
      const keen = this.activeSlider && this.activeSlider.slider;
      const details = keen && keen.track && keen.track.details;
      let atStart = true;
      let atEnd = false;
      if (details) {
        const minIdx = typeof details.minIdx === 'number' ? details.minIdx : 0;
        const maxIdx = typeof details.maxIdx === 'number'
          ? details.maxIdx
          : (details.slides ? details.slides.length - 1 : 0);
        atStart = details.rel <= minIdx;
        atEnd = details.rel >= maxIdx;
      }
      this.prevButton.classList.toggle('ia-visibility-hidden', atStart);
      this.nextButton.classList.toggle('ia-visibility-hidden', atEnd);
    }

    handleBlockSelect(event) {
      const item = event.target.closest && event.target.closest('.ia-slider-tabs__item');
      if (!item || !this.contains(item)) return;
      const index = this.items.indexOf(item);
      if (index >= 0) this.activateTab(index);
    }
  }

  customElements.define('ia-slider-tabs', IaSliderTabs);
}
